import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { IAuftragLieferung , IAuftrag, IAuftragChangeStatusRequest, IAuftragHinweisRequest, IAuftragHinweisResponse, IAuftragMessage, IAuftragRequest, IAuftragTerminiereRequest, IAuftragWiedervorlageRequest, IDienstleistungenFromAuftrag, IGetAuftraegeRequestDto, ISearchAuftragRequest, WriteAuftragMessageDto } from "../types/Types";
import { ErrorAPI } from "./ErrorAPI";

/**
 * @author mb
 */
const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL! + process.env.REACT_APP_API_VERSION! + "auftrag",
    credentials: 'include'
});

const baseQueryWithErrorHandling = async (args: any, api: any, extraOptions: any) => {
    const result = await baseQuery(args, api, extraOptions);
    
    if (result.error) {
        const errorMessage = (result.error?.data as any)?.text || (result.error?.data as any)?.message || 'Unknown error';
        const status = (result.error as any)?.status || 'UNKNOWN';
        //console.error(status, errorMessage);
        
        // Log error via ErrorAPI
        try {
            await api.dispatch(ErrorAPI.endpoints.createLog.initiate({
                status: String(status),
                api: "auftrag",
                message: errorMessage
            }));
        } catch (logError) {
            console.error('Failed to log error:', logError);
        }
    }
    return result;
}

export const AuftragAPI = createApi({
    reducerPath: "auftragAPI",
    baseQuery: baseQueryWithErrorHandling,
    tagTypes: ['auftragAPI'], // for caching
    endpoints: (builder) => ({
        getEigeneAuftraege: builder.mutation<any, void>(
            {
                query: () => ({
                    url: 'eigeneauftraege',
                    method: 'get',
                    params: {

                    },
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        neuerAuftrag: builder.mutation<IAuftrag, IAuftragRequest>(
            {
                query: (dto: IAuftragRequest) => ({
                    url: '',
                    method: 'post',
                    credentials: 'include',
                    body: dto
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        getAuftrag: builder.mutation<IAuftrag, string>(
            {
                query: (uid) => ({
                    url: '' + uid,
                    method: 'get',
                    params: {
                    },
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        getAuftraege: builder.mutation<IAuftrag[], IGetAuftraegeRequestDto>(
            {
                query: (dto) => ({
                    url: 'list',
                    method: 'post',
                    params: {
                        //"status": uid
                    },
                    credentials: 'include',
                    body: dto
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        getAuftragFiles: builder.mutation<any, string>(
            {
                query: (uid) => ({
                    url: uid + '/files',
                    method: 'get',
                    params: {

                    },
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        speicherAuftrag: builder.mutation<any, IAuftragRequest>(
            {
                query: (request) => ({
                    url: '',
                    method: 'post',
                    body: request,
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        changeStatus: builder.mutation<IAuftrag, IAuftragChangeStatusRequest>(
            {
                query: (request) => ({
                    url: 'changeStatus',
                    method: 'post',
                    body: request,
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        /*statusGruppiert: builder.mutation<any, void>(
            {
                query: (request) => ({
                    url: 'statusGruppiert',
                    method: 'get',
                    body: request,
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),*/
        termin: builder.mutation<IAuftrag, IAuftragTerminiereRequest>(
            {
                query: (request) => ({
                    url: 'termin',
                    method: 'post',
                    body: request,
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        terminStorno: builder.mutation<void, string>(
            {
                query: (uid) => ({
                    url: 'terminStorno/' + uid,
                    method: 'post',
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        wiedervorlage: builder.mutation<IAuftrag, IAuftragWiedervorlageRequest>(
            {
                query: (request) => ({
                    url: 'wiedervorlage',
                    method: 'post',
                    body: request,
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        wiedervorlageStorno: builder.mutation<void, string>(
            {
                query: (uid) => ({
                    url: 'wiedervorlageStorno/' + uid,
                    method: 'post',
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        getHistoriePublic: builder.mutation<any, string>(
            {
                query: (uuid) => ({
                    url: 'historiepublic/' + uuid,
                    method: 'get',
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        getHistoriePrivate: builder.mutation<any, string>(
            {
                query: (uuid) => ({
                    url: 'historieprivate/' + uuid,
                    method: 'get',
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        getAnzahlBeiAdresse: builder.mutation<any, string>(
            {
                query: (uuid) => ({
                    url: 'anzahlBeiAdresse/' + uuid,
                    method: 'get',
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        ausfuehrungMirZuordnen: builder.mutation<any, string>(
            {
                query: (uuid) => ({
                    url: 'ausfuehrungMirZuordnen/' + uuid,
                    method: 'post',
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        ausfuehrungZuordnenStorno: builder.mutation<any, string>(
            {
                query: (uuid) => ({
                    url: 'ausfuehrungZuordnenStorno/' + uuid,
                    method: 'post',
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        getHinweise: builder.mutation<IAuftragHinweisResponse[], string>(
            {
                query: (uuid) => ({
                    url: 'hinweise/' + uuid,
                    method: 'get',
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        createHinweis: builder.mutation<any, IAuftragHinweisRequest>(
            {
                query: (dto) => ({
                    url: 'hinweise/',
                    method: 'post',
                    credentials: 'include',
                    body: dto,
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        getHinweiseFuerAG: builder.mutation<IAuftragHinweisResponse[], string>(
            {
                query: (uuid) => ({
                    url: 'hinweisefuerag/' + uuid,
                    method: 'get',
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        getTermine: builder.mutation<any, any>(
            {
                query: (dto) => ({
                    url: 'termine/',
                    method: 'post',
                    credentials: 'include',
                    body: dto,
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        changeColor: builder.mutation<void, { uid: string, color: string }>(
            {
                query: ({ uid, color }) => ({
                    url: 'changeColor/' + uid + '/' + color,
                    method: 'get',
                    credentials: 'include',
                    //body: dto,
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        getDownloadNaechsterWerktag: builder.mutation<{ blob: Blob; filename: string }, any>(
            {
                query: (dto) => ({
                    url: 'getFilesNaechsterWerktag',
                    method: 'post',
                    credentials: 'include',
                    body: dto,
                    responseHandler: async (response: Response) => {
                        const blob = await response.blob();
                        const disposition = response.headers.get('Content-Disposition');
                        const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] ?? 'naechsterWerktag.zip';
                        return { blob, filename };
                    },
                    headers: {
                        'Content-Type': 'application/json',
                    }
                })
            }
        ),
        getDownloadVonBis: builder.mutation<{ blob: Blob; filename: string }, { von: Date, bis: Date, ausfuehrenderUid: string | undefined }>(
            {
                query: (dto) => ({
                    url: 'getFilesVonBis',
                    method: 'post',
                    credentials: 'include',
                    body: dto,
                    responseHandler: async (response: Response) => {
                        const blob = await response.blob();
                        const disposition = response.headers.get('Content-Disposition');
                        const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] ?? 'naechsterWerktag.zip';
                        return { blob, filename };
                    },
                    headers: {
                        'Content-Type': 'application/json',
                    }
                })
            }
        ),
        getDownloadAuftragsDateienAsZip: builder.mutation<{ blob: Blob; filename: string }, string>(
            {
                query: (uid) => ({
                    url: '/' + uid + '/getZipFile',
                    method: 'post',
                    credentials: 'include',
                    responseHandler: async (response: Response) => {
                        const blob = await response.blob();
                        const disposition = response.headers.get('Content-Disposition');
                        const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] ?? 'download.zip';
                        return { blob, filename };
                    },
                    headers: {
                        'Content-Type': 'application/json',
                    }
                })
            }
        ),
        searchAuftraege: builder.mutation<IAuftrag[], ISearchAuftragRequest>(
            {
                query: (dto) => ({
                    url: 'searchAuftraege',
                    method: 'post',
                    body: dto,
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                })
            }
        ),
        getAuftragMessages: builder.mutation<IAuftragMessage[], string>(
            {
                query: (uid) => ({
                    url: 'nachrichten/' + uid,
                    method: 'get',
                    credentials: 'include',
                }),
            }
        ),
        writeAuftragMessage: builder.mutation<void, { uid: string, dto: WriteAuftragMessageDto }>(
            {
                query: ({ uid, dto }) => ({
                    url: 'nachrichten/' + uid,
                    method: 'post',
                    body: dto,
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        unpinnAuftrag: builder.mutation<void, string>(
            {
                query: (uid) => ({
                    url: 'unpinn/' + uid,
                    method: 'post',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        removeAuftragFile: builder.mutation<any, { auftragUid: string, fileUid: string }>(
            {
                query: ({ auftragUid, fileUid }) => ({
                    url: '/removeFile/' + auftragUid + "/" + fileUid,
                    method: 'delete',
                    credentials: 'include'
                }),
                invalidatesTags: ["auftragAPI"],
            }
        ),
        storniereAuftrag: builder.mutation<void, string>(
            {
                query: (auftragUid) => ({
                    url: `storno/` + auftragUid,
                    method: 'post',
                    credentials: 'include'
                })
            }
        ),

        getDeckblattPDF: builder.mutation<{ blob: Blob; filename: string }, any>(
            {
                query: (uid: string) => ({
                    url: 'deckblattPDF/' + uid,
                    method: 'get',
                    credentials: 'include',
                    responseHandler: async (response: Response) => {
                        const blob = await response.blob();
                        const disposition = response.headers.get('Content-Disposition');
                        const filename = disposition?.match(/filename="?(.+?)"?/)?.[1] ?? 'deckblatt.pdf';
                        return { blob, filename };
                    },
                    headers: {
                        'Content-Type': 'application/json',
                    }
                })
            }
        ),

        saveCalendarEventId: builder.mutation<void, { auftragUid: string, eventId: string }>(
            {
                query: (dto) => ({
                    url: `saveCalendarEventId/` + dto.auftragUid,
                    method: 'post',
                    credentials: 'include',
                    body: dto.eventId,
                })
            }
        ),
        getAuftragDienstleistungen: builder.mutation<IDienstleistungenFromAuftrag[], string>(
            {
                query: (auftraguid) => ({
                    url: auftraguid + `/dienstleistungen`,
                    method: 'get',
                    credentials: 'include',
                })
            }
        ),
        addDienstleistungZuAuftrag: builder.mutation<void, { uid_auftrag: string, uid_dstl: string }>(
            {
                query: (dto) => ({
                    url: dto.uid_auftrag + '/dienstleistungZuAuftrag/' + dto.uid_dstl,
                    method: 'put',
                    credentials: 'include'
                })
            }
        ),
        deaktivateDienstleistungZuAuftrag: builder.mutation<void, { uid_auftrag: string, uid_dstl: string }>(
            {
                query: (dto) => ({
                    url: dto.uid_auftrag + '/dienstleistungZuAuftrag/' + dto.uid_dstl,
                    method: 'delete',
                    credentials: 'include'
                })
            }
        ),
        getLieferungenListe: builder.mutation<IAuftragLieferung[], void>(
            {
                query: () => ({
                    url: '/lieferungen/',
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        terminSync: builder.mutation<void, void>(
            {
                query: () => ({
                    url: '/termin-sync',
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        addressIsValid: builder.mutation<void, string>(
            {
                query: (uid) => ({
                    url: '/' + uid + '/addressIsValid/',
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
        addressIsNotValid: builder.mutation<void, string>(
            {
                query: (uid) => ({
                    url: '/' + uid + '/addressIsNotValid/',
                    method: 'get',
                    credentials: 'include'
                })
            }
        ),
         changeVisibilityForVerkaeufer: builder.mutation<void, { uid : string , visible: boolean}>(
            {
                query: (dto) => ({
                    url: '/visibilityForVerkaeufer/',
                    method: 'post',
                    credentials: 'include',
                    body: { uid: dto.uid , visible: dto.visible}
                })
            }
        ),
    })
});

export const {

    useGetEigeneAuftraegeMutation,
    useNeuerAuftragMutation,
    useGetAuftragMutation,
    useGetAuftraegeMutation,
    useGetAuftragFilesMutation,
    useSpeicherAuftragMutation,
    useChangeStatusMutation,
    //useStatusGruppiertMutation,
    useTerminMutation,
    useTerminStornoMutation,
    useWiedervorlageMutation,
    useWiedervorlageStornoMutation,
    useGetHistoriePublicMutation,
    useGetHistoriePrivateMutation,
    useGetAnzahlBeiAdresseMutation,
    useAusfuehrungMirZuordnenMutation,
    useAusfuehrungZuordnenStornoMutation,
    useGetHinweiseMutation,
    useGetHinweiseFuerAGMutation,
    useCreateHinweisMutation,
    useGetTermineMutation,
    useChangeColorMutation,
    useGetDownloadNaechsterWerktagMutation,
    useGetDownloadVonBisMutation,
    useGetDownloadAuftragsDateienAsZipMutation,
    useSearchAuftraegeMutation,
    useGetAuftragMessagesMutation,
    useWriteAuftragMessageMutation,
    useUnpinnAuftragMutation,
    useRemoveAuftragFileMutation,
    useStorniereAuftragMutation,
    useGetDeckblattPDFMutation,
    useSaveCalendarEventIdMutation,
    useGetAuftragDienstleistungenMutation,
    useAddDienstleistungZuAuftragMutation,
    useDeaktivateDienstleistungZuAuftragMutation,
    useGetLieferungenListeMutation,
    useTerminSyncMutation,
    useAddressIsValidMutation,
    useAddressIsNotValidMutation,
    useChangeVisibilityForVerkaeuferMutation,

} = AuftragAPI;
